import type { LensCatalogueDatasetSummary, LensCatalogueField, LensCatalogueMeasureGroup } from '~/types/leLens';

import { AGGREGATE_OPTIONS } from '../panel/panelTypes';

/** "3 dates · 24 dimensions · 13 measures", the catalogue list row's field-count line. */
export function fieldCountsText(counts: LensCatalogueDatasetSummary['fieldCounts']): string {
  const date = counts.dates === 1 ? 'date' : 'dates';
  return `${counts.dates} ${date} · ${counts.dimensions} dimensions · ${counts.measures} measures`;
}

/** "1 date · 28 dim · 30 meas · daily", the abbreviated facts line for the Data Catalogue's dense
 *  long-tail rows and anchor cards, where the full "dimensions"/"measures" wording of
 *  `fieldCountsText` does not fit a single line at every field-count size. */
export function compactFieldCountsText(dataset: Pick<LensCatalogueDatasetSummary, 'fieldCounts' | 'refresh'>): string {
  const { dates, dimensions, measures } = dataset.fieldCounts;
  const date = dates === 1 ? 'date' : 'dates';
  return `${dates} ${date} · ${dimensions} dim · ${measures} meas · ${dataset.refresh}`;
}

/** The Combine control's own word for a field's declared rule: "Ratio" for a standard ratio
 *  measure, otherwise the aggregate's label (e.g. "Sum", "Count distinct"). */
export function catalogueFieldCombineWord(field: Pick<LensCatalogueField, 'aggregate' | 'ratio'>): string {
  if (field.ratio) {
    return 'Ratio';
  }
  return field.aggregate ? (AGGREGATE_OPTIONS.find((option) => option.value === field.aggregate)?.label ?? '') : '';
}

/** "Margin (before refunds) divided by TTV (before refunds).", each part resolved to its own
 *  label rather than shown as a raw column name. Falls back to the raw name if the referenced
 *  field is not in the same dataset (should not happen for a well-formed registry entry). */
export function catalogueRatioSentence(
  field: Pick<LensCatalogueField, 'ratio'>,
  fieldsByName: ReadonlyMap<string, LensCatalogueField>,
): string | undefined {
  if (!field.ratio) {
    return undefined;
  }
  const of = fieldsByName.get(field.ratio.of)?.label ?? field.ratio.of;
  const per = fieldsByName.get(field.ratio.per)?.label ?? field.ratio.per;
  return `${of} divided by ${per}.`;
}

export function catalogueFieldsByName(fields: ReadonlyArray<LensCatalogueField>): Map<string, LensCatalogueField> {
  return new Map(fields.map((field) => [field.name, field]));
}

/** Plain (non-ratio) measures against the standard-measures (ratio) sub-group, in that order. */
export function splitMeasureFields(fields: ReadonlyArray<LensCatalogueField>): {
  plain: Array<LensCatalogueField>;
  standard: Array<LensCatalogueField>;
} {
  const measures = fields.filter((field) => field.kind === 'measure');
  return {
    plain: measures.filter((field) => !field.ratio),
    standard: measures.filter((field) => field.ratio),
  };
}

/** True when a field's label, raw name or description contains the query, case-insensitively.
 *  An empty (or whitespace-only) query matches everything. */
export function catalogueFieldMatchesQuery(
  field: Pick<LensCatalogueField, 'description' | 'label' | 'name'>,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return (
    field.label.toLowerCase().includes(needle) ||
    field.name.toLowerCase().includes(needle) ||
    (field.description?.toLowerCase().includes(needle) ?? false)
  );
}

/** Fields whose label, raw name or description contains the query. Powers the dataset page's
 *  field search; the parent pre-filters before handing fields to `DatasetFieldGroups`. */
export function filterCatalogueFields<T extends Pick<LensCatalogueField, 'description' | 'label' | 'name'>>(
  fields: ReadonlyArray<T>,
  query: string,
): Array<T> {
  return fields.filter((field) => catalogueFieldMatchesQuery(field, query));
}

/** Splits `text` into matched/unmatched runs against `query`, case-insensitively, so a caller can
 *  wrap the matched runs for a highlight. An empty query returns the whole text as one unmatched
 *  run. Only the first occurrence style (every occurrence, left to right) is marked. */
export function highlightRuns(text: string, query: string): Array<{ text: string; matched: boolean }> {
  const needle = query.trim();
  if (!needle) {
    return [{ matched: false, text }];
  }
  const lowerText = text.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  const runs: Array<{ text: string; matched: boolean }> = [];
  let cursor = 0;
  let index = lowerText.indexOf(lowerNeedle, cursor);
  while (index !== -1) {
    if (index > cursor) {
      runs.push({ matched: false, text: text.slice(cursor, index) });
    }
    runs.push({ matched: true, text: text.slice(index, index + needle.length) });
    cursor = index + needle.length;
    index = lowerText.indexOf(lowerNeedle, cursor);
  }
  if (cursor < text.length) {
    runs.push({ matched: false, text: text.slice(cursor) });
  }
  return runs.length > 0 ? runs : [{ matched: false, text }];
}

/** "TTV (BR) / Orders", the compact form of a ratio measure for an outline pill. Uses a plain
 *  slash rather than "÷": Suisse Int'l does not render the division-sign glyph reliably (it fell
 *  back to a "+"-shaped mark in the live render), so a slash is the safe choice. The drawer keeps
 *  the full sentence from `catalogueRatioSentence`; this is only for the field row's pill. */
export function catalogueRatioPillLabel(
  field: Pick<LensCatalogueField, 'ratio'>,
  fieldsByName: ReadonlyMap<string, LensCatalogueField>,
): string | undefined {
  if (!field.ratio) {
    return undefined;
  }
  const of = fieldsByName.get(field.ratio.of)?.label ?? field.ratio.of;
  const per = fieldsByName.get(field.ratio.per)?.label ?? field.ratio.per;
  return `${of} / ${per}`;
}

/** "41 (1 date, 25 dimensions, 12 measures)", the dataset facts panel's Fields line. */
export function datasetFactsFieldsLine(
  fieldCount: number,
  counts: LensCatalogueDatasetSummary['fieldCounts'],
): string {
  return `${fieldCount} (${counts.dates} date, ${counts.dimensions} dimensions, ${counts.measures} measures)`;
}

/**
 * Reads a signal the API contract does not declare on the type yet (a certified flag, today),
 * without an unsound `any` cast. Returns false until the server adds the field; once it does,
 * this starts returning real values with no caller change beyond widening the type.
 */
export function hasUndeclaredFlag<T extends object>(value: T, key: string): boolean {
  const record = value as unknown as Record<string, unknown>;
  return key in record && Boolean(record[key]);
}

export const MEASURE_GROUP_ORDER: ReadonlyArray<LensCatalogueMeasureGroup> = ['money', 'count', 'rate'];

export const MEASURE_GROUP_LABEL: Record<LensCatalogueMeasureGroup, string> = {
  count: 'Counts',
  money: 'Money',
  rate: 'Rates',
};

/** "What you can measure": a governed dataset's measures split Money / Counts / Rates, the
 *  picker's replacement for the old dialog's six-chips-and-a-"+9" row. A field with no declared
 *  group (should not happen once the registry sets it) falls back by shape: a ratio is a rate,
 *  everything else a count. */
export function groupMeasuresByKind(
  fields: ReadonlyArray<LensCatalogueField>,
): Array<[LensCatalogueMeasureGroup, Array<string>]> {
  const byGroup: Record<LensCatalogueMeasureGroup, Array<string>> = { count: [], money: [], rate: [] };
  fields
    .filter((field) => field.kind === 'measure')
    .forEach((field) => {
      const group = field.group ?? (field.ratio ? 'rate' : 'count');
      byGroup[group].push(field.label);
    });
  return MEASURE_GROUP_ORDER.map(
    (group) => [group, byGroup[group]] as [LensCatalogueMeasureGroup, Array<string>],
  ).filter(([, labels]) => labels.length > 0);
}
